import { useEffect, useMemo, useState } from "react";
import { api, mxn, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { EmptyState, Btn } from "@/components/Kit";
import { toast } from "sonner";
import { Search, X, FileText, AlertTriangle, Link2, CheckCircle2 } from "lucide-react";
import { ConciliacionPagos } from "@/components/ConciliacionPagos";

export default function Conciliar() {
  const [tab, setTab] = useState("remisiones");
  const [banks, setBanks] = useState([]);
  const [clients, setClients] = useState([]);
  const [df, setDf] = useState("");
  const [dt, setDt] = useState("");
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [candSel, setCandSel] = useState({});
  const [relatingId, setRelatingId] = useState(null);

  const clientOptions = useMemo(
    () => [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })),
    [clients]
  );

  const fetchData = (params = {}) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (params.date_from) p.set("date_from", params.date_from);
    if (params.date_to) p.set("date_to", params.date_to);
    if (params.client_id) p.set("client_id", params.client_id);
    api.get(`/reconciliation${p.toString() ? `?${p.toString()}` : ""}`)
      .then((r) => setData(r.data))
      .catch(() => setData(false))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/clients").then((r) => setClients([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }))));
    api.get("/payment-banks").then((r) => setBanks(r.data.map((b) => b.name)));
    fetchData();
  }, []);

  const createBank = async (name) => {
    try {
      await api.post("/payment-banks", { name });
      const r = await api.get("/payment-banks");
      setBanks(r.data.map((b) => b.name));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const apply = () => fetchData({ date_from: df, date_to: dt, client_id: cid });
  const clear = () => { setDf(""); setDt(""); setCid(""); fetchData(); };

  const toggleCand = (invId, remId) =>
    setCandSel((prev) => {
      const cur = new Set(prev[invId] || []);
      if (cur.has(remId)) cur.delete(remId); else cur.add(remId);
      return { ...prev, [invId]: cur };
    });

  const relate = async (invId) => {
    const ids = [...(candSel[invId] || [])];
    if (ids.length === 0) return toast.error("Selecciona al menos una remisión candidata");
    setRelatingId(invId);
    try {
      const { data: res } = await api.post(`/invoices/${invId}/link-remisiones`, { remision_ids: ids });
      toast.success(`${res.linked} remisión(es) relacionada(s)`);
      setCandSel((prev) => ({ ...prev, [invId]: new Set() }));
      apply();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRelatingId(null);
    }
  };
  const openReport = () => {
    const p = new URLSearchParams();
    if (df) p.set("date_from", df);
    if (dt) p.set("date_to", dt);
    if (cid) p.set("client_id", cid);
    window.open(`/reporte-conciliacion?${p.toString()}`, "_blank");
  };

  const totals = data?.totals || {};

  return (
    <>
      <PageHeader overline="Auditoría" title="Conciliar" />
      <div className="p-10" data-testid="conciliar-page">
        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#E3E8EE] mb-6">
          <button onClick={() => setTab("remisiones")} data-testid="tab-remisiones"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "remisiones" ? "border-[#465B7F] text-[#465B7F]" : "border-transparent text-[#5B6672] hover:text-[#16202B]"}`}>
            Remisiones vs Facturas
          </button>
          <button onClick={() => setTab("pagos")} data-testid="tab-pagos"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "pagos" ? "border-[#465B7F] text-[#465B7F]" : "border-transparent text-[#5B6672] hover:text-[#16202B]"}`}>
            Conciliación de Pagos
          </button>
        </div>

        {tab === "pagos" ? (
          <ConciliacionPagos banks={banks} onCreateBank={createBank} />
        ) : (
        <>
        <div className="border border-[#465B7F]/25 bg-[#465B7F]/5 px-4 py-3 mb-6 font-mono-data text-xs text-[#465B7F]">
          Reporte de auditoría que cruza Remisiones y Facturas. En <b>Facturas sin remisión</b> puedes revisar las posibles coincidencias y <b>relacionarlas</b> directamente.
        </div>

        {/* Filtros */}
        <div className="sticky top-0 z-20 border border-[#E3E8EE] bg-white p-4 mb-6 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="overline">Desde</span>
            <input type="date" value={df} onChange={(e) => setDf(e.target.value)} data-testid="conciliar-date-from"
              className="bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="overline">Hasta</span>
            <input type="date" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="conciliar-date-to"
              className="bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="overline">Cliente</span>
            <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="conciliar-client"
              className="bg-white border border-[#E3E8EE] px-2 py-1.5 font-mono-data text-sm max-w-[14rem] focus:outline-none focus:ring-1 focus:ring-[#465B7F]">
              <option value="">Todos</option>
              {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <button onClick={apply} data-testid="conciliar-apply"
            className="inline-flex items-center gap-1.5 bg-[#16202B] text-white px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-black transition-colors">
            <Search size={13} /> Filtrar
          </button>
          <button onClick={clear} data-testid="conciliar-clear"
            className="inline-flex items-center gap-1.5 text-[#5B6672] px-2 py-1.5 text-xs uppercase tracking-wider hover:text-[#16202B]">
            <X size={13} /> Limpiar
          </button>
          <button onClick={openReport} data-testid="conciliar-report"
            className="ml-auto inline-flex items-center gap-1.5 border border-[#465B7F] text-[#465B7F] px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-[#465B7F] hover:text-white transition-colors">
            <FileText size={13} /> Reporte
          </button>
        </div>

        {loading || data === null ? (
          <p className="font-mono-data text-sm text-[#5B6672] py-10 text-center">Generando conciliación…</p>
        ) : data === false ? (
          <p className="font-mono-data text-sm text-[#ED1F26] py-10 text-center">No se pudo generar la conciliación.</p>
        ) : (
          <div className="space-y-8">
            {/* Resumen */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="conciliar-summary">
              <SummaryCard label="Remisiones con coincidencia" count={totals.matched} amount={totals.matched_total} tone="green" />
              <SummaryCard label="Remisiones sin coincidencia" count={totals.remisiones_sin_factura} amount={totals.remisiones_sin_factura_total} tone="amber" />
              <SummaryCard label="Facturas sin remisión" count={totals.facturas_sin_remision} amount={totals.facturas_sin_remision_total} tone="red" />
            </div>

            {/* Bloque 1: Remisiones con coincidencia */}
            <Section icon={<CheckCircle2 size={16} className="text-[#49AC9A]" />} title="Remisiones con coincidencia" count={data.matched.length}>
              {data.matched.length === 0 ? (
                <EmptyRow msg="Sin remisiones con factura relacionada en este filtro." />
              ) : (
                <table className="w-full" data-testid="conciliar-matched-table">
                  <thead><tr className="border-b border-[#E3E8EE] text-left">
                    <Th>Remisión</Th><Th>Factura</Th><Th>Cliente</Th><Th>Coincidencia</Th><Th className="text-right">Monto</Th>
                  </tr></thead>
                  <tbody>
                    {data.matched.map((m) => (
                      <tr key={m.remision_id} data-testid={`conciliar-matched-${m.remision_number}`} className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3]">
                        <Td className="text-[#1C1A17] font-medium">{m.remision_number}</Td>
                        <Td><span className="inline-flex items-center gap-1 text-[#465B7F]"><Link2 size={12} /> {m.invoice_number}</span></Td>
                        <Td>{m.client_name || "—"}</Td>
                        <Td><span className="text-xs uppercase tracking-wider px-1.5 py-0.5 bg-[#49AC9A]/15 text-[#2f7a6c] border border-[#49AC9A]/30">{m.match_type}</span></Td>
                        <Td className="text-right text-[#1C1A17]">{mxn(m.remision_total)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {/* Bloque 2: Remisiones sin coincidencia */}
            <Section icon={<AlertTriangle size={16} className="text-[#B8860B]" />} title="Remisiones sin coincidencia" count={data.remisiones_sin_factura.length}>
              {data.remisiones_sin_factura.length === 0 ? (
                <EmptyRow msg="Todas las remisiones tienen una factura relacionada." />
              ) : (
                <table className="w-full" data-testid="conciliar-rem-sin-table">
                  <thead><tr className="border-b border-[#E3E8EE] text-left">
                    <Th>Remisión</Th><Th>Cliente</Th><Th className="text-right">Monto</Th>
                  </tr></thead>
                  <tbody>
                    {data.remisiones_sin_factura.map((r) => (
                      <tr key={r.remision_id} data-testid={`conciliar-rem-sin-${r.remision_number}`} className="border-b border-[#E3E8EE] last:border-0 bg-[#FCF6E3] hover:bg-[#F7EFD2]">
                        <Td className="text-[#1C1A17] font-medium">{r.remision_number}</Td>
                        <Td>{r.client_name || "—"}</Td>
                        <Td className="text-right text-[#1C1A17]">{mxn(r.remision_total)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {/* Bloque 3: Facturas sin remisión (crítico) con posibles coincidencias */}
            <Section icon={<AlertTriangle size={16} className="text-[#ED1F26]" />} title="Facturas sin remisión asociada" count={data.facturas_sin_remision.length} critical>
              {data.facturas_sin_remision.length === 0 ? (
                <EmptyRow msg="Todas las facturas tienen al menos una remisión relacionada." />
              ) : (
                <div className="divide-y divide-[#E3E8EE]" data-testid="conciliar-fac-sin-list">
                  {data.facturas_sin_remision.map((f) => {
                    const sel = candSel[f.invoice_id] || new Set();
                    return (
                      <div key={f.invoice_id} data-testid={`conciliar-fac-sin-${f.invoice_number}`} className="p-4">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className={`inline-flex items-center gap-1 font-mono-data text-sm font-medium ${f.linked ? "text-[#2f7a6c]" : "text-[#ED1F26]"}`}>
                            {f.linked ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {f.invoice_number}
                          </span>
                          <span className={`font-mono-data text-sm flex-1 min-w-[8rem] truncate ${f.linked ? "text-[#5B6672]" : "text-[#8f2d2d]"}`}>{f.client_name || "— (sin cliente)"}</span>
                          {f.linked && (f.remision_numbers || []).length > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-[#49AC9A]/15 text-[#2f7a6c] border border-[#49AC9A]/30">
                              <Link2 size={11} /> {f.remision_numbers.join(", ")}
                            </span>
                          )}
                          <span className={`font-mono-data text-sm ${f.linked ? "text-[#16202B]" : "text-[#ED1F26]"}`}>{mxn(f.total)}</span>
                        </div>

                        {f.linked ? null : f.candidates && f.candidates.length > 0 ? (
                          <div className="mt-3 border border-[#E3E8EE] bg-[#F7F9FB]">
                            <div className="px-3 py-2 border-b border-[#E3E8EE] flex items-center justify-between">
                              <span className="overline">Posibles coincidencias ±$5.00 ({f.candidates.length})</span>
                              <Btn
                                onClick={() => relate(f.invoice_id)}
                                disabled={relatingId === f.invoice_id || sel.size === 0}
                                data-testid={`relate-invoice-${f.invoice_number}`}
                              >
                                <Link2 size={14} /> {relatingId === f.invoice_id ? "Relacionando…" : `Relacionar (${sel.size})`}
                              </Btn>
                            </div>
                            <div className="divide-y divide-[#E3E8EE]">
                              {f.candidates.map((c) => (
                                <label key={c.remision_id} data-testid={`candidate-${f.invoice_number}-${c.remision_number}`}
                                  className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#EEF2F6]">
                                  <input type="checkbox" checked={sel.has(c.remision_id)} onChange={() => toggleCand(f.invoice_id, c.remision_id)}
                                    data-testid={`candidate-check-${f.invoice_number}-${c.remision_number}`} className="h-4 w-4 accent-[#465B7F]" />
                                  <span className="font-mono-data text-sm text-[#16202B] w-24">{c.remision_number}</span>
                                  <span className="font-mono-data text-xs text-[#5B6672] flex-1 truncate">{c.client_name || "—"}</span>
                                  <span className="text-xs uppercase tracking-wider px-1.5 py-0.5 bg-[#465B7F]/10 text-[#465B7F] border border-[#465B7F]/25">{c.match_type}</span>
                                  <span className="font-mono-data text-sm text-[#16202B] w-28 text-right">{mxn(c.total)}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="mt-2 font-mono-data text-xs text-[#8A94A0]">Sin remisiones candidatas (importe ±$5.00).</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>
          </div>
        )}

        {data && data !== false && !data.matched.length && !data.remisiones_sin_factura.length && !data.facturas_sin_remision.length && (
          <EmptyState title="Sin datos para conciliar" subtitle="Ajusta los filtros o registra remisiones y facturas." />
        )}
        </>
        )}
      </div>
    </>
  );
}

function SummaryCard({ label, count, amount, tone }) {
  const tones = {
    green: "border-[#49AC9A]/40 text-[#2f7a6c]",
    amber: "border-[#B8860B]/40 text-[#B8860B]",
    red: "border-[#ED1F26]/40 text-[#ED1F26]",
  };
  return (
    <div className={`border ${tones[tone]} bg-white p-4`} data-testid={`conciliar-card-${tone}`}>
      <div className="overline">{label}</div>
      <div className="font-display font-extrabold text-3xl mt-1">{count ?? 0}</div>
      <div className="font-mono-data text-sm mt-1">{mxn(amount || 0)}</div>
    </div>
  );
}

function Section({ icon, title, count, critical, children }) {
  return (
    <div className={`border ${critical ? "border-[#ED1F26]/30" : "border-[#E3E8EE]"} bg-white`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E3E8EE]">
        {icon}
        <span className="font-display font-bold text-[#16202B]">{title}</span>
        <span className="ml-auto font-mono-data text-xs text-[#5B6672]">{count} registro(s)</span>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function EmptyRow({ msg }) {
  return <p className="font-mono-data text-sm text-[#5B6672] py-6 px-4 text-center">{msg}</p>;
}
function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`font-mono-data text-sm text-[#5C5A55] px-4 py-3 ${className}`}>{children}</td>;
}
